/* eslint-disable */

import Cookies from "js-cookie";
import axios from 'axios';
import Response from './models/Response';
import { translate } from '@/composables/translate';

let globalLastRefresh = 0;
let refreshPromise = null;
export default class ApiClient {
    requestResource;
    methods = {
        GET: 1,
        POST: 2,
        PUT: 3,
        DELETE: 4
    }

    /**
     * Inicializates ApiClient
     * @param {String} relativeUrl
     * @param {Boolean} authRequest
     * @param {Boolean} requestErrorHandler
     */
    constructor(relativeUrl = '/', authRequest = true, requestErrorHandler = true) {
        this.baseUrl = `${process.env.VUE_APP_API_URL}${relativeUrl}`;
        this.confAxios(authRequest, requestErrorHandler);
    }

    /**
     * Configurates this axios instance, setting it to 'requestResourse' attr
     * @param {Boolean} authRequest 
     * @param {Boolean} requestErrorHandler 
     */
    confAxios(authRequest, requestErrorHandler) {
        this.requestResource = axios.create({
            baseURL: this.baseUrl,
            headers: {
                'Accept': 'application/json',
                "Content-Type": "application/json"
            }
        });

        this.setThresholdInterceptor();

        if (authRequest) {
            this.setRequestAuthorizationInterceptor();
        }
        if (requestErrorHandler)
            this.setRequestErrorHandlerInterceptor();
    }

    setThresholdInterceptor() {
        const REFRESH_THRESHOLD = parseInt(process.env.VUE_APP_REFRESH_THRESHOLD || 0);

        this.requestResource.interceptors.request.use(async (config) => {
            if (config.url.includes('regenerate')) {
                return config;
            }

            const now = Date.now();
            const authStore = this.getStores().authStore;

            if (authStore.isAuthenticated && (now - globalLastRefresh > REFRESH_THRESHOLD)) {

                if (!refreshPromise) {
                    refreshPromise = this._doRefresh(authStore).finally(() => {
                        refreshPromise = null;
                    });
                }

                await refreshPromise;
            }

            return config;
        });
    }

    async _doRefresh(authStore) {
        try {
            const authApiClient = new ApiClient('api/auth/');
            const response = await authApiClient.executeRequest('/regenerate', {}, this.methods.POST);

            if (response.success()) {
                const newToken = response.getContent().token || response.getHeader('authorization');
                if (newToken) {
                    authStore.saveToken(newToken);
                    globalLastRefresh = Date.now();
                    Cookies.set('TM_TOKEN', newToken);
                }
            } else {
                authStore.logout();
                this.redirect('/login');
            }
        } catch (err) {
            console.error("Falha fatal ao deslizar sessão", err);
        }
    }

    /**
     * 
     */
    setRequestAuthorizationInterceptor() {
        this.requestResource.interceptors.request.use(
            config => {
                const token = Cookies.get('TM_TOKEN');
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            error => {
                return Promise.reject(error);
            }
        );
    }

    setRequestErrorHandlerInterceptor() {
        this.requestResource.interceptors.response.use(
            response => response,
            error => {
                if (error.response == undefined) {
                    return {
                        data: {},
                        resMessage: translate('a problem occurred with your application, reload the page or contact the suport'),
                        status: 900
                    };
                } else {
                    throw error;
                }
            }
        );
    }

    /**
     * Perform an AXIOS request
     * @param {String} url 
     * @param {Object} parameters 
     * @param {Integer} method - this.methods
     * @param {Object} header
     * @returns {Response}
     */
    async executeRequest(url, parameters = {}, method = this.methods.GET, options = {}) {
        const requestOptions = {
            params: method === 'get' ? parameters : undefined,
            responseType: options.responseType || 'json',
            headers: options.headers || {}
        };

        try {
            const response = await this._makeRequest(url, parameters, method, requestOptions);

            if (response.apiError()) {
                this._handleMaintenance();
                return response;
            }
            if (response.notAuthenticated()) {
                // this._handleMaintenance();
                return response;
            }

            return response;

        } catch (error) {
            return this._handleError(error, new Response({ code: 500, message: 'A problem occurred with your application, try again or contact the suport' }));
        }
    }

    /**
     * Builds the request accordingly to method
     * @param {String} url 
     * @param {Object} parameters 
     * @param {String} method 
     * @param {Object} options 
     * @returns 
     */
    async _makeRequest(url, parameters = {}, method, options) {
        parameters.user_lang = this.getStores().contextStore.getLang;

        let requestResults = {};
        switch (method) {
            case this.methods.GET:
                requestResults = await this.requestResource.get(this._buildGetUrl(url, parameters), options);
                break;
            case this.methods.POST:
                requestResults = await this.requestResource.post(url, parameters, options);
                break;
            case this.methods.PUT:
                requestResults = await this.requestResource.put(url, parameters, options);
                break;
            case this.methods.DELETE:
                requestResults = await this.requestResource.delete(url, parameters, options);
                break;
            default:
                throw new Error(`Método ${method} não suportado.`);
        }

        return new Response(requestResults, true);
    }

    /**
     * Builds GET urls
     * @param {String} baseUrl 
     * @param {Object} params 
     * @returns String
     */
    _buildGetUrl(baseUrl, params) {
        const query = new URLSearchParams();
        for (const key in params) {
            const value = params[key];
            if (Array.isArray(value)) {
                value.forEach((v, index) => query.append(`${key}[${index}]`, v));
            } else {
                query.append(key, value);
            }
        }
        return `${baseUrl}?${query.toString()}`;
    }

    /**
     * Saves session message and redirect to maintance page
     */
    _handleMaintenance() {
        // store.dispatch('saveSessionMessage', {
        //     message: 'A problem occurred with your application, but we are already looking into it',
        //     messageType: 'error'
        // });
        // this.redirect('/maintance');
    }

    /**
     * Saves session message, logout and redirect to login
     */
    _handleExpiredSession() {
        if (this.getStores().authStore.isAuthenticated) {
            this.getStores().messageStore.errorMessage(translate('Authentication expired', 'capitalize'));
        }

        const authStore = this.getStores().authStore;
        authStore.logout();
        Cookies.remove('TM_TOKEN');
        Cookies.remove('TK_DATE');

        this.redirect('/login');
    }

    /**
     * Handle request errors
     * @param {*} error 
     * @param {*} resultFormat 
     * @returns 
     */
    _handleError(error, resultFormat) {
        const defaultErrorMessage = 'Unexpected error, please try again later';

        resultFormat.success = false;
        resultFormat.data = '';
        resultFormat.status = 500;

        if (error?.response) {
            resultFormat.data = error.response.data;
            resultFormat.status = error.response.status;
            resultFormat.message = error.response.data?.message || defaultErrorMessage;

            if (resultFormat.status === 419) {
                this.getStores().messageStore.errorMessage(translate('Login expired, please log in again.'));
                resultFormat.message = translate('Login expired, please log in again.', 'ucfirst');
                this.redirect('/login');
            }

            if (resultFormat.status === 488) {
                const authStore = this.getStores().authStore;
                authStore.logout();
                Cookies.remove('TM_TOKEN');
                Cookies.remove('TK_DATE');
                this.getStores().messageStore.errorMessage(translate('login expired', 'ucfirst'));
                resultFormat.message = translate('login expired', 'ucfirst');
                this.redirect('/login');
            }

            if (resultFormat.status === 401) {
                this._handleExpiredSession();
            }
        } else {
            resultFormat.message = defaultErrorMessage;
        }

        return new Response({ status: resultFormat.status, message: resultFormat.message, data: resultFormat.data });
    }

    redirect(url = '') {
        import('@/router').then(({ default: router }) => {
            router.push({ path: url });
        });
    }

    getStores() {
        const { useContextStore } = require('@/stores/context');
        const { useAuthStore } = require('@/stores/auth');
        const { useSessionMessageStore } = require('@/stores/sessionMessage');

        return {
            contextStore: useContextStore(),
            authStore: useAuthStore(),
            messageStore: useSessionMessageStore()
        }
    }
}